<script lang="ts">
    import { createBubbler, preventDefault } from 'svelte/legacy';
    import { onMount } from 'svelte';

    import type { SettingFormDefinition } from 'header/types';
    import SettingsFieldGroup from './SettingsFieldGroup.svelte';

    interface Props {
        definition: SettingFormDefinition;
    }

    const bubble = createBubbler();
    let { definition }: Props = $props();

    let tabs = $derived([
        ...Object.entries(definition.fields).filter(([_, tabFields]) => tabFields.length),
    ]);
    let json = $derived(JSON.stringify(definition, null, 2));

    let page = $state(0);

    onMount(definition.forceLoadAllLazySettings);

    function handleFieldChange(event: CustomEvent) {
        definition.onChange(event.detail.field);
    }

    function handleTabChange(newPage: number) {
        page = newPage;
    }

    function getTabName(tabKey: string) {
        switch (tabKey) {
            case 'userSettingFields':
                return 'User settings';
            case 'designerSettingFields':
                return 'Designer settings';
            default:
                return tabKey;
        }
    }
</script>

<form class="form" onsubmit={preventDefault(bubble('submit'))}>
    <ul class="form__tabs" role="tablist">
        {#each tabs as [tabKey, _], tabIndex (tabKey)}
            <li class="form__tab">
                <button
                    class:active={tabIndex === page}
                    role="tab"
                    id={`tab-${tabKey}`}
                    aria-controls={`panel-${tabKey}`}
                    aria-selected={tabIndex === page}
                    onclick={() => handleTabChange(tabIndex)}
                >
                    {getTabName(tabKey)}
                </button>
            </li>
        {/each}
    </ul>
    <div class="form__panels">
        {#each tabs as [tabKey, tabFields], tabIndex (tabKey)}
            <section
                class="form__panel"
                hidden={tabIndex !== page}
                role="tabpanel"
                id={`panel-${tabKey}`}
                aria-labelledby={`tab-${tabKey}`}
            >
                <SettingsFieldGroup
                    nolabel={false}
                    settingDefinitions={tabFields}
                    on:change={handleFieldChange}
                />
            </section>
        {/each}
    </div>
    <details class="json">
        <summary>JSON</summary>
        <pre>{json}</pre>
    </details>
</form>

<style lang="scss" scoped>
    .form {
        &__tabs {
            display: flex;
            align-items: center;
            column-gap: 1px;
            padding: 0;
            list-style: none;
            margin-bottom: 1rem;
            border-bottom: 1px solid #000;
        }
        &__tab {
            overflow: hidden;

            button {
                display: block;
                overflow: hidden;
                max-width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
                text-align: center;
                font-size: 0.8rem;
                padding: 0.2rem 0.3rem;
                border: 1px solid black;
                border-bottom: none;
                border-radius: 4px 4px 0 0;
                background: transparent;

                &:hover {
                    background-color: #ccc;
                }
                &.active {
                    color: #fff;
                    background-color: #000;
                }
                &:not(.active) {
                    cursor: pointer;
                }
            }
        }
    }

    .json {
        padding-top: 1rem;
        margin-top: 1rem;
        font-size: 0.8rem;
        border-top: 1px solid #000;

        pre {
            overflow-x: auto;
        }
    }
</style>
